import { config } from './config';
import { AuthService } from './services/auth-service';
import { ProfileService } from './services/profile-service';
import { TripService } from './services/trip-service';
import { MediaService } from './services/media-service';
import { NotificationService } from './services/notification-service';
import { FlutterUtils } from "@/hooks/useFlutterBridge";
import { CallResponse, UserProfile, Trip } from './types';
import { LocationService } from './services/location-service';
import { TicketService } from './services/ticket-service';

export type HeyLinkConfig = {
  clientName: string;
  clientDisplayName: string;
  clientLogoUrl: string;
  baseUrl: string;
  baseUrlOag: string;
  GATackingID: string;
  oagLogin: string;
  oagPassword: string;
  techLogin: string;
  techPassword: string;
  debug?: string;
  featuresFlags?: {
    [key: string]: string;
  };
};

const _LOADING_HEY_LINK_CONFIG: HeyLinkConfig = {
  clientName: 'loading',
  clientDisplayName: 'Loading ...',
  clientLogoUrl: '',
  baseUrl: '',
  baseUrlOag: '',
  GATackingID: '',
  oagLogin: '',
  oagPassword: '',
  techLogin: '',
  techPassword: ''
};

const _LOCALDEV_CMT_TEST_HEY_LINK_CONFIG: HeyLinkConfig = {
  "clientName": "CMT",
  "clientDisplayName": "CheckMyTrip (Test)",
  "clientLogoUrl": "https://preview--travel-titans.lovable.app/lovable-uploads/abb0412f-b739-4de2-8ac5-33dd0597bc98.png",
  "baseUrl": "https://api-test.getfeasy.com/hey/api",
  "baseUrlOag": "https://api-test.getfeasy.com/hey-g",
  "GATackingID": "G-PDWF6Y822E",
  "techLogin": "robot-mob@checkmytrip.com",
  "techPassword": "iQ9ORMTlRrWDbIuQQNlSRe8uP2",
  "oagLogin": "oag_new",
  "oagPassword": "b4B!e0Q5l*6Qxw!!hc",
  "debug": "Y"
}

export class HeyUtils {
  // Core properties
  userAccessToken: string;
  refreshToken: string;
  expiresAt: number;
  user_id: string;
  user_email: string;
  techToken: string;
  oagToken: string;
  techTokenExpires: number;
  oagTokenExpires: number;
  firebaseToken: string = null;

  // Service instances - make authService and tripService public
  public authService: AuthService;
  private profileService: ProfileService;
  public tripService: TripService;
  private mediaService: MediaService;
  private notificationService: NotificationService;
  private locationService: LocationService;
  private ticketService: TicketService;

  private static _heyLinkUnknownOrigin : boolean = false;
  private static _heyLinkConfig: HeyLinkConfig = null;
  private static _heyLinkConfigTTL: number = 1000 * 60 * 5; // 5 min

  private isInitialized: boolean = false;

  // Cache properties
  _cachedProfile: UserProfile;
  _cachedProfileTimestamp: number;
  _cachedTrips: Trip[];
  _cachedTripsTimestamp: number;

  // Singleton instance
  static instance: HeyUtils;

  static async waitInit() {
    return new Promise((resolve) => {
      let instance = HeyUtils.getInstance();
      const checkInstance = () => {
        if (HeyUtils.instance && HeyUtils.instance.isInitialized) {
          resolve(true);
        } else {
          setTimeout(checkInstance, 100);
        }
      };
      checkInstance();
    });
  }

  static getInstance(): HeyUtils {
    if (!HeyUtils.instance) {
      HeyUtils.instance = new HeyUtils();
    }
    return HeyUtils.instance;
  }

  static isOriginUnknown(): boolean {
    return HeyUtils._heyLinkUnknownOrigin && !HeyUtils._heyLinkConfig;
  }

  constructor() {
    this.initInstance();
  }

  public getTripCacheTimestamp(): number {
    if (this._cachedTripsTimestamp) {
      return this._cachedTripsTimestamp;
    }
    return 0;
  }

  private async initInstance() {
    await this.loadUserTokenForLocalStorage();
    await this.retrieveConfig();
    
    // Initialize services
    this.authService = new AuthService(this);
    this.profileService = new ProfileService(this);
    this.tripService = new TripService(this);
    this.mediaService = new MediaService(this);
    this.notificationService = new NotificationService(this);
    this.locationService = new LocationService(this);
    this.ticketService = new TicketService(this);
    this.isInitialized = true;
  }

  public async waitInit() {
    if (!this.isInitialized) {
      console.debug('Waiting for HeyUtils to initialize...');
      while (!this.isInitialized) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      console.debug('HeyUtils initialized');
    }
  }
  
  public getUserId(): string {
    if (this.isUserAuthenticated()) {
      return this.user_id;
    }
    return '';
  }

  public getConfig() : HeyLinkConfig {
    if (this.isLocalDev()) {
      if (_LOADING_HEY_LINK_CONFIG.debug == "N") {
        console.debug = () => {};
      }
      return _LOCALDEV_CMT_TEST_HEY_LINK_CONFIG;
    }
    if (HeyUtils._heyLinkConfig === null) {
      console.error('Configuration is null, please check the API response');
      return _LOADING_HEY_LINK_CONFIG;
    }
    return HeyUtils._heyLinkConfig;
  }

  private isLocalDev() {
    return location.origin.indexOf("localhost") !== -1 
      || location.origin.indexOf("192.168.") !== -1
      || location.origin.indexOf("172.16.") !== -1
      || location.origin.indexOf("127.0.0.1") !== -1;
  }

  private async retrieveConfig() {
    // if cache is null get from local storage
    if (HeyUtils._heyLinkConfig === null) {
      const cachedConfig = localStorage.getItem(config.LS_KEY_CONFIG_CACHE);
      if (cachedConfig) {
        let cachedConf = JSON.parse(cachedConfig);
        if (cachedConf) {
          this.updateConfig(cachedConf.data);
          let cacheDate = new Date(cachedConf.date);
          if (cacheDate.getTime() + HeyUtils._heyLinkConfigTTL > Date.now()) {
            console.debug('✅ Using cached configuration from local storage and no need to refresh');
          }
          else 
          {
            console.debug('⏱️ Cached configuration expired, using it and refreshing from API');
            this.refreshConfigAsync();
          }
        }
      }
    }

    if (!HeyUtils._heyLinkConfig) {
      await this.refreshConfigAsync();
    }
  }

  private applyConfig() {
    console.debug('Applying configuration...');
    if (HeyUtils._heyLinkConfig) {
      document.title = HeyUtils._heyLinkConfig.clientDisplayName
      console.debug('✅ Configuration applied successfully');
    } else {
      console.error('❌ Failed to apply configuration');
    }
  }

  private async refreshConfigAsync() {
    console.debug('🔄 Fetching configuration from API...');
    try {
      if (this.isLocalDev()) {
        return this.handleRetrievedConfig(_LOCALDEV_CMT_TEST_HEY_LINK_CONFIG);
      }

      let response = await fetch(config.GET_CONF_API, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          try {
            let data = await response.json();
            if (data.message.toLowerCase().includes('invalid origin')) {
              console.error('❌ Origin not allowed to fetch configuration', location.origin);
              HeyUtils._heyLinkUnknownOrigin = true;
              return null;
            }
          } catch (error) {
            console.error('❌ Config Response is not Json', error);
          }
        }
        console.error(`❌ Failed to fetch configuration, continue with cached one: ${response.status} ${response.statusText}`);
        return null;
      }
      const newConfig = await response.json();
      return this.handleRetrievedConfig(newConfig);
    } catch (error) {
      console.error('❌ Error fetching configuration:', error);
      return null;
    }
  }
  
  private handleRetrievedConfig(newConfig: HeyLinkConfig) {
    HeyUtils._heyLinkUnknownOrigin = false;
    try {
      localStorage.setItem(config.LS_KEY_CONFIG_CACHE, JSON.stringify({
        date: new Date(),
        data: newConfig
      }));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      localStorage.clear();
    }

    if (newConfig.debug == "Y")
    {
      console.log('Debug mode enabled');
      console.debug('Configuration:', newConfig);
    }
    else
    {
      console.debug = () => {};
    }

    // Check if the configuration has changed
    if (JSON.stringify(HeyUtils._heyLinkConfig) !== JSON.stringify(newConfig)) {
      console.debug('⚠️ Configuration has changed, updating cache...');
      this.handleHeyLinkConfigChange(newConfig);
      console.debug('✅ Configuration fetched from API and cached');
    } else {
      console.debug('✅ Configuration is unchanged, keeping cache');
    }
    this.updateConfig(newConfig);
    return newConfig;
  }

  private updateConfig(newConfig: HeyLinkConfig) {
    console.debug('Updating configuration...');
    HeyUtils._heyLinkConfig = newConfig;
    this.applyConfig();
  }
  
  private handleHeyLinkConfigChange(newConfig: any) {
    
    // window.location.reload();
  }

  public isUserAuthenticated(): boolean {
    return this.techToken && 
           this.userAccessToken && 
           this.userAccessToken.length > 10 && 
           Date.now() < this.expiresAt && 
           this.user_id && 
           this.user_id.length > 5;
  }

  public async signOut(): Promise<boolean> {
    console.debug('Signing out user...');
    this.userAccessToken = '';
    this.refreshToken = '';
    this.user_id = '';
    this.user_email = '';
    this.expiresAt = 0;
    localStorage.removeItem(config.LS_KEY_TOKEN);
    localStorage.removeItem(config.LS_KEY_REFRESH_TOKEN);
    localStorage.removeItem(config.LS_KEY_PUBLIC_EMAIL);
    localStorage.removeItem(config.LS_KEY_PUBLIC_ID);
    localStorage.removeItem(config.LS_KEY_TOKEN_EXPIRES);
    localStorage.removeItem(config.LS_KEY_TECH_TOKEN);
    localStorage.removeItem(config.LS_KEY_REFRESH_TOKEN);
    localStorage.removeItem("userName");
    return true;
  }

  private async loadUserTokenForLocalStorage() {
    const token = localStorage.getItem(config.LS_KEY_TOKEN);
    const techToken = localStorage.getItem(config.LS_KEY_TECH_TOKEN);
    const publicId = localStorage.getItem(config.LS_KEY_PUBLIC_ID);
    const userEmail = localStorage.getItem(config.LS_KEY_PUBLIC_EMAIL);
    const refreshToken = localStorage.getItem(config.LS_KEY_REFRESH_TOKEN);
    const expires = localStorage.getItem(config.LS_KEY_TOKEN_EXPIRES);

    if (!techToken || !token || !refreshToken || !expires || !publicId) {
      console.debug('No token found in local storage');
      return { success: false, error: 'No token found' };
    }

    this.techToken = techToken;
    this.userAccessToken = token;
    this.user_id = publicId;
    this.user_email = userEmail;

    const expiry = parseInt(expires);
    if (Date.now() > expiry && this.authService) {
      await this.authService.refreshToken();
    } else {
      this.userAccessToken = token;
      this.refreshToken = refreshToken;
      this.expiresAt = parseInt(expires);
    }

    FlutterUtils.requestFirebaseToken();
    return {
      success: true,
    };
  }

  // Public API methods - delegating to appropriate services
  
  // Auth methods
  public async checkUserLogin(): Promise<boolean> {
    return this.authService.checkUserLogin();
  }

  public async loginUserPassword(email: string, password: string): Promise<CallResponse> {
    return this.authService.loginUserPassword(email, password);
  }

  public async requestOTP(email: string): Promise<CallResponse> {
    return this.authService.requestOTP(email);
  }

  public async validateOTP(code: string, email: string): Promise<CallResponse> {
    return this.authService.validateOTP(code, email);
  }

  public async registerUser(request: any): Promise<CallResponse> {
    return this.authService.registerUser(request);
  }

  public async enableNotifications(user_id: string): Promise<CallResponse> {
    return this.authService.enableNotifications(user_id);
  }

  public async setUserPassword(email: string, password: string): Promise<CallResponse> {
    return this.authService.setUserPassword(email, password);
  }

  public async updatePassword(newPassword: string): Promise<boolean> {
    return this.authService.updatePassword(newPassword);
  }

  // Profile methods
  public async getUserProfile(): Promise<UserProfile> {
    return this.profileService.getUserProfile();
  }

  public async updateUser(user: UserProfile): Promise<boolean> {
    return this.profileService.updateUser(user);
  }

  public async deleteAccount(): Promise<boolean> {
    return this.profileService.deleteAccount();
  }

  // Trip methods
  public async getUserTrips(): Promise<Trip[]> {
    return this.tripService.getUserTrips();
  }

  public async importAdtdTrip(adtdData:any): Promise<Trip> {
    return this.tripService.importAdtdTrip(adtdData);
  }

  public async getOfferById(id : string): Promise<Trip[]> {
    return this.tripService.getOfferById(id);
  }

  public async addTripByReference(reference: string): Promise<Trip> {
    return this.tripService.addTripByReference(reference);
  }

  public async addTripManually(tripData: Trip): Promise<Trip> {
    return this.tripService.addTripManually(tripData);
  }

  public async removeTrip(reference: string): Promise<boolean> {
    return this.tripService.removeTrip(reference);
  }

  // Ticket methods
  public async getTicketDocumentReceipt(
    userId: string,
    tripId: string,
    ticketNumber: string,
    languageCode: string = 'en'
  ): Promise<{ documentUrl: string }> {
    return this.ticketService.getTicketDocumentReceipt(userId, tripId, ticketNumber, languageCode);
  }

  public async downloadTicketDocument(documentUrl: string): Promise<void> {
    return this.ticketService.downloadTicketDocument(documentUrl);
  }

  // Media methods
  public async getAirportInfos(iataCode: string, format: string): Promise<any> {
    return this.mediaService.getAirportInfos(iataCode, format);
  }

  public async getFlightDetails(carrierCode: string, flightNumber: string, departureDate: string): Promise<any> {
    return this.mediaService.getFlightDetails(carrierCode, flightNumber, departureDate);
  }

  public async getWeatherData(latitude: number, longitude: number): Promise<any> {
    return this.mediaService.getWeatherData(latitude, longitude);
  }

  public async getWeatherByAirportCode(airportCode: string): Promise<any> {
    console.debug('🌤️ HeyUtils.getWeatherByAirportCode called with code:', airportCode);
    try {
      const result = await this.mediaService.getWeatherByAirportCode(airportCode);
      console.debug('🌤️ HeyUtils.getWeatherByAirportCode result received');
      return result;
    } catch (error) {
      console.error('🌤️ HeyUtils.getWeatherByAirportCode error:', error);
      throw error;
    }
  }

  public async getActivities(tripId: string, filters?: any, catalog: string = "discover"): Promise<any> {
    return this.mediaService.getActivities(tripId, filters, catalog);
  }

  // Notification methods
  public async updateUserPushToken(token: string, platform: string): Promise<boolean> {
    return this.notificationService.updateUserPushToken(token, platform);
  }

  // Add a public method to call the API directly
  public async callHeyApiUser(method: string, url: string, body: any): Promise<CallResponse> {
    return this.authService.callHeyApiUser(method, url, body);
  }

  public async getCitiesByCountry(keyword: string, countryCode: string): Promise<string[]> {
    return this.locationService.getCitiesByCountry(keyword, countryCode);
  }
}
